import React, { useCallback, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { CopyIcon } from '@modernloop/shared/icons';
import { Box, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';

import { ActivityLogAgentDebug_ActivityLogFragment } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

interface Fragments {
  activityLog: ActivityLogAgentDebug_ActivityLogFragment;
}

const ActivityLogAgentDebug: FCWithFragments<Fragments> = ({ activityLog }) => {
  const { t } = useTranslation();
  const aiTaskEnabled = useIsAiTaskEnabled();
  const { details } = activityLog;
  // eslint-disable-next-line no-underscore-dangle
  const json = details?.__typename === 'ActivityLogAgentDebugDetails' ? details.json : '';
  const [expanded, setExpanded] = useState(false);

  const formattedJson = useMemo(() => {
    try {
      return json ? JSON.stringify(JSON.parse(json), null, 2) : 'No debug data';
    } catch {
      return json || 'No debug data';
    }
  }, [json]);

  const handleCopy = useCallback(async () => {
    await navigator.clipboard.writeText(json || 'No debug data');
  }, [json]);

  const toggleExpanded = useCallback(() => {
    setExpanded((current) => !current);
  }, []);

  if (!aiTaskEnabled) {
    return null;
  }

  return (
    <Stack spacing={0.75} sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ minWidth: 0 }}>
        <Typography
          component="span"
          variant="body2"
          onClick={toggleExpanded}
          sx={{
            color: 'text.secondary',
            fontFamily: 'monospace',
            fontWeight: 700,
            fontSize: 13,
            letterSpacing: '0.05em',
            lineHeight: 1.5,
            cursor: 'pointer',
            userSelect: 'none',
          }}
        >
          {t('hiring-dashboard-candidates:candidates.activityLog.debugLogAgentTurnInfo')}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={0.25} sx={{ flexShrink: 0 }}>
          <Tooltip title={t('hiring-dashboard-candidates:candidates.activityLog.copy')}>
            <IconButton
              aria-label={t('hiring-dashboard-candidates:candidates.activityLog.copy')}
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleCopy().catch(() => {});
              }}
              sx={{ p: 0.5, color: 'text.disabled' }}
            >
              <CopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              expanded
                ? t('hiring-dashboard-candidates:candidates.activityLog.collapse')
                : t('hiring-dashboard-candidates:candidates.activityLog.expand')
            }
          >
            <IconButton
              aria-label={
                expanded
                  ? t('hiring-dashboard-candidates:candidates.activityLog.collapse')
                  : t('hiring-dashboard-candidates:candidates.activityLog.expand')
              }
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                toggleExpanded();
              }}
              sx={{ p: 0.5, color: 'text.disabled' }}
            >
              <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: 16, lineHeight: 1 }}>
                {expanded ? '▾' : '▸'}
              </Typography>
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Collapse in={expanded}>
        <Box
          component="pre"
          sx={{
            mt: 0,
            mb: 0,
            px: 1.5,
            py: 1.25,
            bgcolor: 'grey.900',
            color: 'grey.100',
            borderRadius: 1,
            fontSize: 12,
            fontFamily: 'monospace',
            overflow: 'auto',
            maxHeight: 500,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {formattedJson}
        </Box>
      </Collapse>
    </Stack>
  );
};

ActivityLogAgentDebug.fragments = {
  activityLog: gql`
    fragment ActivityLogAgentDebug_activityLog on ActivityLog {
      id
      details {
        ... on ActivityLogAgentDebugDetails {
          __typename
          json
        }
      }
    }
  `,
};

export default ActivityLogAgentDebug;
